import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Loader2 } from 'lucide-react';
import {
  MetricsRow, CompetencyChart, SubjectBreakdownPie,
  TopPerformersCard, AlertsCard, RegionCompetencyLineChart, DrillCard,
} from '../../../components/LevelDashboard';
import type { LevelMetrics, CompetencyBucket, TopPerformer, AlertItem } from '../../../types';
import type { SubjectBucket } from '../../../components/LevelDashboard';
import {
  useGetKvsResultSummaryQuery,
  useGetKvsResult3to5Query,
  useGetKvsResultSubjectwiseQuery,
  useGetKvsResultRegionwiseQuery,
  useGetKvsResultTopPerformersQuery,
  resultsSessionLabel,
} from '../../../services/kvsResultApi';
import { QuerySection, sectionState } from '../../../components/QuerySection';
import { StageScoreBandCard } from '../../../components/ScoreBandPanel';

const BASE = '/kvs-samagam/student-analytics/stages/preparatory';
const levelSlug = (level: string) => level.toLowerCase().replace(/\s+/g, '-');

type ClassFilter = 'overall' | '3' | '4' | '5';

const HolisticDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [academicYear, setAcademicYear] = useState<number>(2026);
  const [term, setTerm] = useState<number>(0);
  const [classFilter, setClassFilter] = useState<ClassFilter>('overall');

  const querySuffix = `?year=${academicYear}&term=${term === 0 ? 'overall' : term}`;

  // ========== API Queries ==========
  // Each section shows its own spinner / error (QuerySection), so a slow or failed
  // request no longer blanks the whole page or masquerades as "No data".
  const summaryQ = useGetKvsResultSummaryQuery({ year: academicYear, term });
  const distQ = useGetKvsResult3to5Query({ year: academicYear, term });
  const subjectQ = useGetKvsResultSubjectwiseQuery({ year: academicYear, term });
  const regionQ = useGetKvsResultRegionwiseQuery({ year: academicYear, term });
  const topQ = useGetKvsResultTopPerformersQuery({ year: academicYear, term, order: 'desc' });
  const bottomQ = useGetKvsResultTopPerformersQuery({ year: academicYear, term, order: 'asc' });
  const summaryRes = summaryQ.data;
  const distRes = distQ.data;
  const subjectRes = subjectQ.data;
  const regionRes = regionQ.data;
  const topRes = topQ.data;
  const bottomRes = bottomQ.data;
  const anyFetching = [summaryQ, distQ, subjectQ, regionQ, topQ, bottomQ].some(q => q.isFetching);

  // ========== Derived Data ==========
  const metrics: LevelMetrics = useMemo(() => {
    const d = summaryRes?.data;
    if (!d) return { totalStudents: 0, avgAttendance: 0, avgCompetency: 0, needsSupportCount: 0, excellentCount: 0, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };
    const src = classFilter === 'overall' ? d : d.byClass?.[classFilter];
    if (!src) return { totalStudents: 0, avgAttendance: 0, avgCompetency: 0, needsSupportCount: 0, excellentCount: 0, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };

    const subjectItems = classFilter === 'overall'
      ? subjectRes?.data?.overall
      : subjectRes?.data?.byClass?.[classFilter];
    const avgCompetency = subjectItems?.length
      ? +(subjectItems.reduce((sum, s) => sum + s.avgWeightage, 0) / subjectItems.length).toFixed(1)
      : +src.avgMarks.toFixed(1);

    return {
      totalStudents: src.totalStudents,
      avgAttendance: 0,
      avgCompetency,
      needsSupportCount: src.needSupport,
      excellentCount: src.excellentStudents,
      avgBehaviorScore: 0,
      avgPeerScore: 0,
      bmiNormalPercent: 0,
    };
  }, [summaryRes, subjectRes, classFilter]);

  const competencyBuckets: CompetencyBucket[] = useMemo(() => {
    const d = distRes?.data;
    if (!d) return [
      { level: 'Excellent', count: 0, percent: 0 },
      { level: 'Good', count: 0, percent: 0 },
      { level: 'Average', count: 0, percent: 0 },
      { level: 'Below Average', count: 0, percent: 0 },
    ];
    const src = classFilter === 'overall' ? d.overall : d.byClass?.[classFilter];
    if (!src) return [
      { level: 'Excellent', count: 0, percent: 0 },
      { level: 'Good', count: 0, percent: 0 },
      { level: 'Average', count: 0, percent: 0 },
      { level: 'Below Average', count: 0, percent: 0 },
    ];
    const total = src.total || 1;
    return [
      { level: 'Excellent', count: src.excellent, percent: +((src.excellent / total) * 100).toFixed(1) },
      { level: 'Good', count: src.good, percent: +((src.good / total) * 100).toFixed(1) },
      { level: 'Average', count: src.average, percent: +((src.average / total) * 100).toFixed(1) },
      { level: 'Below Average', count: src.belowAverage, percent: +((src.belowAverage / total) * 100).toFixed(1) },
    ];
  }, [distRes, classFilter]);

  const subjectData: SubjectBucket[] = useMemo(() => {
    const d = subjectRes?.data;
    if (!d) return [];
    const items = classFilter === 'overall' ? d.overall : d.byClass?.[classFilter];
    if (!items) return [];
    // When the source has no per-subject marks the backend sends a single
    // "Overall" placeholder row (weightage 0, DATA_AMBIGUITY). Drop it so the card
    // hides instead of showing "Overall 0%" — same rule as the Secondary stage.
    return items
      .filter(item => item.subjectTitle !== 'Overall')
      .map(item => ({
        subject: item.subjectTitle,
        avgScore: +item.avgWeightage.toFixed(1),
      }));
  }, [subjectRes, classFilter]);

  const regionCompetencyTrend = useMemo(() => {
    const regions = regionRes?.data?.regions;
    if (!regions) return [];
    return regions.map(r => ({
      regionId: r.roID,
      regionName: r.roName,
      avgCompetency: r.avgMarksPercentage,
      topSchoolName: r.topSchool?.schoolName || `School ${r.topSchool?.schoolID ?? ''}`,
      topSchoolScore: r.topSchool?.avgMarksPercentage ?? 0,
      worstSchoolName: r.bottomSchool?.schoolName || `School ${r.bottomSchool?.schoolID ?? ''}`,
      worstSchoolScore: r.bottomSchool?.avgMarksPercentage ?? 0,
    }));
  }, [regionRes]);

  const topPerformers: TopPerformer[] = useMemo(() => {
    const regions = topRes?.data?.regions;
    if (!regions) return [];
    return regions.slice(0, 5).map((r, i) => ({
      id: `TP-${i}`,
      name: `${r.roName} Region`,
      metric: 'Avg Marks',
      value: r.avgMarksPercentage,
    }));
  }, [topRes]);

  const alertItems: AlertItem[] = useMemo(() => {
    const regions = bottomRes?.data?.regions;
    if (!regions) return [];
    return regions.slice(0, 5).map((r, i) => ({
      id: `AL-${i}`,
      name: `${r.roName} Region`,
      issue: `Avg ${r.avgMarksPercentage}% — ${r.totalStudents.toLocaleString()} students`,
      severity: r.avgMarksPercentage < 60 ? 'high' : r.avgMarksPercentage < 75 ? 'medium' : 'low',
    } as AlertItem));
  }, [bottomRes]);

  // The subject pie sits beside the competency chart; with none to show, the KPI cards
  // take that space. isLoading (first load only), not isFetching, so a filter change —
  // which refetches but keeps the previous data — doesn't make the layout jump.
  const showSubjectPie = subjectData.length > 0 || subjectQ.isLoading || subjectQ.isError;

  const termLabel = term === 0 ? 'Overall' : `Term ${term}`;
  const classLabel = classFilter === 'overall' ? 'Class III–V' : `Class ${classFilter}`;

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(new Set([2025, 2026, currentYear - 1, currentYear, currentYear + 1])).sort((a, b) => b - a);
  }, []);

  const handleChartClick = (level: string) => navigate(`${BASE}/list/regions/${levelSlug(level)}${querySuffix}`);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-3">
            Preparatory Stage — Holistic Development
            {anyFetching && (
              <span role="status" className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
                <Loader2 size={12} className="animate-spin" /> Loading data…
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {classLabel} &mdash; Academic Year {resultsSessionLabel(academicYear)} &mdash; {termLabel} &mdash; {metrics.totalStudents.toLocaleString()} students
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="classFilter" className="text-xs font-medium text-gray-600">Class</label>
            <select
              id="classFilter"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value as ClassFilter)}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall (3–5)</option>
              <option value="3">Class 3</option>
              <option value="4">Class 4</option>
              <option value="5">Class 5</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => setTerm(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="0">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Year</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>{resultsSessionLabel(year)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Students by score band — the Preparatory card on Stage Selection, built from the same summary. */}
      {metrics.totalStudents > 0 && (
        <StageScoreBandCard
          stats={{ totalStudents: metrics.totalStudents, excellentStudents: metrics.excellentCount, needSupport: metrics.needsSupportCount }}
          stage="preparatory"
          // Term does not move these figures: the summary counts each student's final marks %.
          matchesStageCard={classFilter === 'overall'}
        >
          <p>{metrics.totalStudents.toLocaleString()} students, each placed by their overall marks %.</p>
          <p className="text-[12px] text-gray-500">
            Excellent and Needs Support use the same cut-offs as Excellent and Below Average in the chart below; Moderate covers Good and Average.
          </p>
        </StageScoreBandCard>
      )}

      {/* KPI Row — beside the competency chart instead when there is no subject pie. */}
      {showSubjectPie && (
        <QuerySection state={sectionState(summaryQ)} label="summary">
          <MetricsRow m={metrics} />
        </QuerySection>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuerySection state={sectionState(distQ)} label="distribution">
          <CompetencyChart buckets={competencyBuckets} onBarClick={handleChartClick} />
        </QuerySection>
        {showSubjectPie ? (
          <QuerySection state={sectionState(subjectQ)} label="subject-wise data" minHeight={320}>
            {subjectData.length > 0 && (
              <SubjectBreakdownPie data={subjectData} title="Subject-wise Average Weightage" />
            )}
          </QuerySection>
        ) : (
          <QuerySection state={sectionState(summaryQ)} label="summary" className="order-first lg:order-none">
            <MetricsRow m={metrics} layout="grid" />
          </QuerySection>
        )}
      </div>

      {(regionCompetencyTrend.length > 0 || regionQ.isFetching || regionQ.isError) && (
        <QuerySection state={sectionState(regionQ)} label="region trend" minHeight={320}>
          {regionCompetencyTrend.length > 0 && (
            <RegionCompetencyLineChart
              data={regionCompetencyTrend}
              onPointClick={(regionId) => navigate(`${BASE}/region/${regionId}${querySuffix}`)}
            />
          )}
        </QuerySection>
      )}

      {/* Top / Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuerySection state={sectionState(topQ)} label="top performers">
          <TopPerformersCard items={topPerformers} />
        </QuerySection>
        <QuerySection state={sectionState(bottomQ)} label="regions needing attention">
          <AlertsCard
            items={alertItems}
            onViewAll={() => navigate(`${BASE}/list/regions/below-average${querySuffix}`)}
            viewAllLabel="Need Attention Regions"
          />
        </QuerySection>
      </div>

      {/* Regions — Drill Down */}
      {(regionQ.isFetching || regionQ.isError) && !regionRes?.data?.regions?.length && (
        <QuerySection state={sectionState(regionQ)} label="regions" minHeight={160} />
      )}
      {regionRes?.data?.regions && regionRes.data.regions.length > 0 && (
        <QuerySection state={sectionState(regionQ)} label="regions">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Regions — Drill Down</h2>
            <button
              onClick={() => navigate(`${BASE}/list/regions${querySuffix}`)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition"
            >
              <List size={16} /> See All Regions
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {regionRes.data.regions.map(r => (
              <DrillCard
                key={r.roID}
                title={r.roName}
                subtitle={`${r.totalStudents.toLocaleString()} students`}
                metrics={{
                  totalStudents: r.totalStudents,
                  avgAttendance: 0,
                  avgCompetency: r.avgMarksPercentage,
                  needsSupportCount: 0,
                  excellentCount: 0,
                  avgBehaviorScore: 0,
                  avgPeerScore: 0,
                  bmiNormalPercent: 0,
                }}
                alerts={[]}
                navigateTo={`region/${r.roID}${querySuffix}`}
              />
            ))}
          </div>
        </div>
        </QuerySection>
      )}
    </div>
  );
};

export default HolisticDashboard;
